import { useState } from 'react';

/**
 * DoctorAvatar — solid silhouette of a doctor when no photo is available.
 *
 * Two variants:
 *   - Male: scrubs collar + stethoscope + holding clipboard (head+chest crop).
 *   - Female: long coat + stethoscope + side ponytail (head+chest crop).
 * Both are head-and-chest crops so they read clearly at 64×64 and below.
 *
 * Falls back to the gendered silhouette automatically when a stored
 * photo URL fails to load (broken / migrated paths).
 */
export default function DoctorAvatar({ photoUrl, gender, name = '', size = 64, className = '' }) {
  const [broken, setBroken] = useState(false);
  const showPhoto = photoUrl && !broken;

  if (showPhoto) {
    return (
      <img
        src={photoUrl}
        alt={name}
        onError={() => setBroken(true)}
        style={{ width: size, height: size }}
        className={`rounded-2xl object-cover shrink-0 flex-none ${className}`}
        data-testid="doctor-avatar-photo"
      />
    );
  }

  const g = (gender || '').toLowerCase();
  const isFemale = g === 'female' || g === 'f';
  const isMale = g === 'male' || g === 'm';
  const variant = isFemale ? 'female' : isMale ? 'male' : 'neutral';

  const bg = isFemale
    ? 'linear-gradient(135deg, #fdf2f8 0%, #fbcfe8 100%)'
    : isMale
    ? 'linear-gradient(135deg, #eff6ff 0%, #bfdbfe 100%)'
    : 'linear-gradient(135deg, #f1f5f9 0%, #cbd5e1 100%)';
  // Solid fill colour of the silhouette — mirrors the user's reference image
  const fill = isFemale ? '#0f172a' : isMale ? '#0f172a' : '#334155';

  return (
    <div
      style={{ width: size, height: size, background: bg }}
      className={`rounded-2xl shrink-0 flex-none flex items-end justify-center overflow-hidden ${className}`}
      data-testid={`doctor-avatar-${variant}`}
    >
      <svg viewBox="0 0 100 100" width={size} height={size} preserveAspectRatio="xMidYMax meet" aria-hidden="true">
        {isFemale ? (
          <g fill={fill}>
            {/* Head */}
            <path d="M50 14c-7 0-12 5.5-12 13 0 4.5 2 8.5 5 11l-1 4c-1 0-2 0.5-2.5 1.5L37 49c0 1 1 2 2 2h22c1 0 2-1 2-2l-2.5-5.5c-0.5-1-1.5-1.5-2.5-1.5l-1-4c3-2.5 5-6.5 5-11 0-7.5-5-13-12-13z" />
            {/* Side ponytail (right shoulder) */}
            <path d="M61 32c5 1 9 6 9 14 0 8-3 16-7 22l-3-1c2-5 4-12 4-19 0-7-1-13-3-16z" />
            {/* Long lab coat — wide flowing shoulders + body */}
            <path d="M28 56c-7 3-12 10-13 21l-1 23h72l-1-23c-1-11-6-18-13-21l-7-3-2 4c-2 4-9 4-11 0l-2-4-22 3z" />
            {/* Coat opening (narrow V down center) */}
            <path d="M48 60l2 6-2 28h4l-2-28 2-6h-4z" fill={isFemale ? '#fdf2f8' : '#eff6ff'} />
            {/* Stethoscope — loop around the neck and a chest piece on right */}
            <path d="M42 50c-2 4-2 9 0 13l2-1c-1-3-1-7 0-11l-2-1zM58 50c2 4 2 9 0 13l-2-1c1-3 1-7 0-11l2-1z" />
            <circle cx="60" cy="68" r="3" />
            <circle cx="60" cy="68" r="1.5" fill={isFemale ? '#fdf2f8' : '#eff6ff'} />
          </g>
        ) : isMale ? (
          <g fill={fill}>
            {/* Head with short hair */}
            <path d="M50 13c-7 0-12 5.5-12 13 0 5 2 9 5 11.5l-1 3.5c-1 0-2 0.5-2.5 1.5L37 48c0 1 1 2 2 2h22c1 0 2-1 2-2l-2.5-5.5c-0.5-1-1.5-1.5-2.5-1.5l-1-3.5c3-2.5 5-6.5 5-11.5 0-7.5-5-13-12-13z" />
            <path d="M40 22c2-3 5-5 10-5s8 2 10 5c-3-1-7-1.5-10-1.5s-7 0.5-10 1.5z" fill={isFemale ? '#fdf2f8' : '#eff6ff'} />
            {/* Scrub V-neck top — broader squared shoulders */}
            <path d="M30 54c-6 3-10 9-11 18l-1 28h64l-1-28c-1-9-5-15-11-18l-8-3-3 6-8 6-8-6-3-6-10 3z" />
            {/* V-neck cut */}
            <path d="M50 50l-7 8 7 6 7-6-7-8z" fill={isFemale ? '#fdf2f8' : '#eff6ff'} />
            {/* Stethoscope — V around neck, ear pieces tucked into collar */}
            <path d="M40 46c-2 5-2 10 0 14l2-1c-1-4-1-8 0-12l-2-1zM60 46c2 5 2 10 0 14l-2-1c1-4 1-8 0-12l2-1z" />
            <circle cx="40" cy="62" r="3" />
            <circle cx="40" cy="62" r="1.5" fill={isFemale ? '#fdf2f8' : '#eff6ff'} />
            {/* Clipboard held on right side */}
            <rect x="64" y="64" width="14" height="18" rx="1.5" />
            <rect x="66" y="62" width="10" height="3" rx="0.6" fill={isFemale ? '#fdf2f8' : '#eff6ff'} />
            <rect x="66" y="69" width="10" height="1.2" fill={isFemale ? '#fdf2f8' : '#eff6ff'} />
            <rect x="66" y="72" width="10" height="1.2" fill={isFemale ? '#fdf2f8' : '#eff6ff'} />
            <rect x="66" y="75" width="6" height="1.2" fill={isFemale ? '#fdf2f8' : '#eff6ff'} />
          </g>
        ) : (
          <g fill={fill}>
            <ellipse cx="50" cy="32" rx="14" ry="16" />
            <path d="M22 96c2-18 13-30 28-30s26 12 28 30" />
          </g>
        )}
      </svg>
    </div>
  );
}
